import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STAGES = ["new", "to_contact", "contacted", "prequalification", "interview_scheduled", "interview_completed",
  "evaluation", "orientation", "training", "ready_deployment", "active", "progressing", "not_retained", "inactive"];
const VERTICALS = ["REAL_ESTATE", "INSURANCE_FINANCIAL_SERVICES", "SALES_CAMPAIGNS", "CHARITY_CAMPAIGNS",
  "AUTOMOTIVE", "BUSINESS_DEVELOPMENT", "PROJECTS_PARTNERSHIPS"];

export default function AdminCandidates() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [recruiters, setRecruiters] = useState([]);
  const [filters, setFilters] = useState({ q: "", stage: "", vertical: "", recruiter_id: "", country: "", unassigned: "" });

  const canSeeAll = ["super_admin", "admin", "manager"].includes(user?.role);

  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    const { data } = await api.get("/candidates", { params });
    if (seq !== requestSeq.current) return; // a newer request already answered
    setItems(data.items || []);
    setTotal(data.total || 0);
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (canSeeAll) {
      api.get("/admin/recruiters").then(({ data }) => setRecruiters(data)).catch(() => setRecruiters([]));
    }
  }, [canSeeAll]);

  const set = (key) => (event) => setFilters((prev) => ({ ...prev, [key]: event.target.value }));

  return (
    <div data-testid="admin-candidates">
      <h1 className="font-display text-3xl font-light sm:text-4xl">{t("admin.candidates")}</h1>

      {!canSeeAll && (
        <label className="mt-4 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            data-testid="candidates-unassigned-pool"
            checked={filters.unassigned === "true"}
            onChange={(event) => setFilters((prev) => ({ ...prev, unassigned: event.target.checked ? "true" : "" }))}
          />
          {t("admin.unassignedPool")}
        </label>
      )}

      <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Input data-testid="candidates-search" placeholder={t("admin.searchPlaceholder")} value={filters.q} onChange={set("q")} />
        <select data-testid="candidates-filter-stage" value={filters.stage} onChange={set("stage")} className="h-10 border border-input bg-surface px-3 text-sm">
          <option value="">{t("admin.filterStage")} — {t("common.all")}</option>
          {STAGES.map((stage) => (
            <option key={stage} value={stage}>{t(`stages.${stage}`)}</option>
          ))}
        </select>
        <select data-testid="candidates-filter-vertical" value={filters.vertical} onChange={set("vertical")} className="h-10 border border-input bg-surface px-3 text-sm">
          <option value="">{t("admin.filterVertical")} — {t("common.all")}</option>
          {VERTICALS.map((vertical) => (
            <option key={vertical} value={vertical}>{t(`verticals.${vertical}`)}</option>
          ))}
        </select>
        {canSeeAll && (
          <select data-testid="candidates-filter-recruiter" value={filters.recruiter_id} onChange={set("recruiter_id")} className="h-10 border border-input bg-surface px-3 text-sm">
            <option value="">{t("admin.filterRecruiter")} — {t("common.all")}</option>
            {recruiters.map((recruiter) => (
              <option key={recruiter.id} value={recruiter.id}>
                {recruiter.first_name} {recruiter.last_name}
              </option>
            ))}
          </select>
        )}
        <div className="flex gap-2">
          <Input data-testid="candidates-filter-country" placeholder={t("admin.filterCountry")} value={filters.country} onChange={set("country")} />
          <Button type="button" variant="outline" data-testid="candidates-clear-filters"
                  onClick={() => setFilters({ q: "", stage: "", vertical: "", recruiter_id: "", country: "", unassigned: "" })}>
            {t("common.clear")}
          </Button>
        </div>
      </div>

      <p data-testid="candidates-count" className="mt-4 text-xs text-muted-foreground">
        {total} {t("admin.resultsCount")}
      </p>

      {items.length === 0 ? (
        <p data-testid="candidates-empty" className="mt-8 text-sm text-muted-foreground">{t("admin.noResults")}</p>
      ) : (
        <div className="mt-4 overflow-x-auto border border-border bg-surface">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-muted text-left">
              <tr>
                <th className="p-3 font-medium">{t("admin.table.name")}</th>
                <th className="p-3 font-medium">{t("admin.table.contact")}</th>
                <th className="p-3 font-medium">{t("admin.table.location")}</th>
                <th className="p-3 font-medium">{t("admin.table.stage")}</th>
                <th className="p-3 font-medium">{t("admin.table.recruiter")}</th>
                <th className="p-3 font-medium">{t("admin.table.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((candidate) => (
                <tr key={candidate.id} data-testid={`candidate-row-${candidate.id}`} className="border-t border-border hover:bg-muted/50">
                  <td className="p-3">
                    <Link to={`/${lang}/admin/candidates/${candidate.id}`} className="hover:text-accent">
                      {candidate.first_name} {candidate.last_name}
                    </Link>
                    {candidate.is_demo && <span className="ml-2 text-[10px] uppercase tracking-widest text-accent">demo</span>}
                  </td>
                  <td className="p-3 text-muted-foreground">
                    <div>{candidate.email}</div>
                    <div className="text-xs">{candidate.phone}</div>
                  </td>
                  <td className="p-3 text-muted-foreground">
                    {candidate.city}, {candidate.province_state}
                    <div className="text-xs">{candidate.country}</div>
                  </td>
                  <td className="p-3">{t(`stages.${candidate.pipeline_stage}`)}</td>
                  <td className="p-3 text-muted-foreground">{candidate.assigned_recruiter_name || t("admin.unassigned")}</td>
                  <td className="p-3">
                    <Link data-testid={`candidate-view-${candidate.id}`} to={`/${lang}/admin/candidates/${candidate.id}`} className="text-accent hover:underline">
                      {t("admin.view")}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
